import { Box, Button, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import TextField from 'components/formik/TextField';
import useOneDriveFilePicker from 'components/@home/@settings/Tabs/integrations/useOneDriveFilePicker';
import { Field } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import Http from 'services/Http';
import { getOAuthTargetOrigin } from 'utils/webOrigin';
import { I18n } from 'utils/i18n';

const getStyles = theme => ({
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  description: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
  },
  oauthButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  connectedInfo: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
  },
  pickerButton: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
});

const MicrosoftExcelConfig = ({ values, setFieldValue, errors, submitCount }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const worksheetError = submitCount > 0 && errors?.configuration?.worksheetName;
  const tokenIdError = submitCount > 0 && errors?.configuration?.tokenId;
  const [isConnecting, setIsConnecting] = useState(false);
  const [worksheets, setWorksheets] = useState([]);
  const [loadingWorksheets, setLoadingWorksheets] = useState(false);

  const isConnected = !!values.configuration?.tokenId;

  const loadWorksheets = async (driveId, fileId, tokenId) => {
    try {
      setLoadingWorksheets(true);
      const { data } = await Http.post('/v1/microsoft-oauth/worksheets', {
        tokenId,
        driveId,
        fileId,
      });
      setWorksheets(data.worksheets);
    } catch (error) {
      console.error('Error loading worksheets:', error);
      setWorksheets([]);
    } finally {
      setLoadingWorksheets(false);
    }
  };

  // Load worksheets when file is selected
  useEffect(() => {
    if (
      values.configuration?.fileId &&
      values.configuration?.driveId &&
      values.configuration?.tokenId
    ) {
      loadWorksheets(
        values.configuration.driveId,
        values.configuration.fileId,
        values.configuration.tokenId,
      );
    }
  }, [values.configuration?.fileId, values.configuration?.driveId, values.configuration?.tokenId]);

  const handleMicrosoftAuth = async () => {
    try {
      setIsConnecting(true);

      // Get auth URL from backend
      const { data } = await Http.get('/v1/microsoft-oauth/auth-url');

      // Open in new tab for better UX (removed noopener to allow communication)
      const authTab = window.open(data.authUrl, 'microsoft-oauth');

      if (!authTab) {
        console.error('Unable to open Microsoft OAuth window');
        setIsConnecting(false);
        return;
      }

      // Listen for message from auth tab
      const handleMessage = event => {
        if (event.source !== authTab) return;

        if (event.origin !== getOAuthTargetOrigin()) return;

        if (event.data.type === 'MICROSOFT_OAUTH_SUCCESS') {
          const { tokenId, userInfo, sharePointUrl } = event.data;

          // Update form values
          setFieldValue('configuration.tokenId', tokenId);
          setFieldValue('configuration.userEmail', userInfo.email);

          // Store SharePoint URL for picker
          if (sharePointUrl) {
            setFieldValue('configuration.sharePointUrl', sharePointUrl);
          }

          setIsConnecting(false);

          // Remove event listener
          window.removeEventListener('message', handleMessage);
        } else if (event.data.type === 'MICROSOFT_OAUTH_ERROR') {
          console.error('Microsoft OAuth error:', event.data.error);
          setIsConnecting(false);
          window.removeEventListener('message', handleMessage);
        }
      };

      window.addEventListener('message', handleMessage);

      // Optional: Focus the auth tab
      if (authTab) {
        authTab.focus();
      }
    } catch (error) {
      console.error('Microsoft OAuth error:', error);
      setIsConnecting(false);
    }
  };

  const handleFileSelection = files => {
    let file;
    if (Array.isArray(files)) {
      file = files[0];
    } else if (files && files.value && Array.isArray(files.value)) {
      file = files.value[0];
    } else if (files && files.length > 0) {
      file = files[0];
    }

    if (file) {
      const driveId = file.parentReference?.driveId || file.driveId || file.drive?.id || '';
      setFieldValue('configuration.fileId', file.id);
      setFieldValue('configuration.driveId', driveId);
      setFieldValue('configuration.selectedFileName', file.name || file.fileName);
      setFieldValue('configuration.worksheetName', '');
    }
  };

  const {
    open: openFilePicker,
    error: pickerError,
    PickerPortal,
  } = useOneDriveFilePicker({
    mode: 'files',
    tokenId: values.configuration?.tokenId,
    getSharePointUrl: async () => values.configuration?.sharePointUrl || null,
    onPick: items => {
      const files = items.map(item => ({
        id: item.id,
        name: item.name,
        parentReference: item.parentReference || {},
        driveId: item.parentReference?.driveId || '',
      }));
      handleFileSelection(files);
    },
  });

  const handleDisconnect = () => {
    setFieldValue('configuration.tokenId', '');
    setFieldValue('configuration.userEmail', '');
    setFieldValue('configuration.driveId', '');
    setFieldValue('configuration.fileId', '');
    setFieldValue('configuration.worksheetName', '');
    setFieldValue('configuration.selectedFileName', '');
    setFieldValue('configuration.sharePointUrl', '');
    setWorksheets([]);
  };

  return (
    <>
      <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Microsoft Account')}</Typography>
      <Typography sx={styles.description}>
        {I18n.t('ChatFlowSettings.Connect your Microsoft account to access Excel files')}
      </Typography>

      {isConnected ? (
        <Box sx={styles.connectedInfo}>
          <Typography variant="body2">
            {I18n.t('ChatFlowSettings.Connected as email', {
              email: values.configuration?.userEmail,
            })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleDisconnect}
            style={{ marginTop: 8 }}
          >
            {I18n.t('ChatFlowSettings.Disconnect')}
          </Button>
        </Box>
      ) : (
        <>
          <Button
            variant="contained"
            color="primary"
            onClick={handleMicrosoftAuth}
            disabled={isConnecting}
            sx={styles.oauthButton}
          >
            {isConnecting
              ? I18n.t('ChatFlowSettings.Connecting...')
              : I18n.t('ChatFlowSettings.Connect Microsoft Account')}
          </Button>
          {tokenIdError && (
            <Typography color="error" variant="body2" sx={{ mt: 1 }}>
              {tokenIdError}
            </Typography>
          )}
        </>
      )}

      {isConnected && (
        <>
          <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Excel File')}</Typography>
          <Typography sx={styles.description}>
            {I18n.t(
              'ChatFlowSettings.Select an Excel file using the file picker or enter IDs manually',
            )}
          </Typography>

          <Button variant="outlined" onClick={openFilePicker} sx={styles.pickerButton}>
            {values.configuration?.selectedFileName
              ? I18n.t('ChatFlowSettings.Selected file', {
                  name: values.configuration.selectedFileName,
                })
              : I18n.t('ChatFlowSettings.Choose Excel File')}
          </Button>

          {pickerError && (
            <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
              {pickerError}
            </Typography>
          )}

          {values.configuration?.fileId && (
            <FormControl
              fullWidth
              size="small"
              error={!loadingWorksheets && !!worksheetError}
              margin="normal"
            >
              <InputLabel>
                {loadingWorksheets
                  ? I18n.t('ChatFlowSettings.Loading worksheets')
                  : I18n.t('ChatFlowSettings.Select worksheet')}
              </InputLabel>
              <Select
                size="small"
                value={values.configuration?.worksheetName || ''}
                onChange={e => setFieldValue('configuration.worksheetName', e.target.value)}
                label={
                  loadingWorksheets
                    ? I18n.t('ChatFlowSettings.Loading worksheets')
                    : I18n.t('ChatFlowSettings.Select worksheet')
                }
                disabled={loadingWorksheets}
                error={!loadingWorksheets && !!worksheetError}
              >
                {worksheets.length === 0 ? (
                  <MenuItem value="" disabled>
                    <em>{I18n.t('ChatFlowSettings.No worksheets found')}</em>
                  </MenuItem>
                ) : (
                  [
                    <MenuItem key="" value="">
                      <em>{I18n.t('ChatFlowSettings.Select worksheet')}</em>
                    </MenuItem>,
                    ...worksheets.map(worksheet => (
                      <MenuItem key={worksheet.name} value={worksheet.name}>
                        {worksheet.name}
                      </MenuItem>
                    )),
                  ]
                )}
              </Select>
              {!loadingWorksheets && worksheetError && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                  {worksheetError}
                </Typography>
              )}
            </FormControl>
          )}

          <Field
            component={TextField}
            name="configuration.driveId"
            label={I18n.t('ChatFlowSettings.Drive ID')}
            placeholder="b!abc123..."
            helperText="Use the picker above or paste the Drive ID manually"
            fullWidth
            margin="normal"
            size="small"
            slotProps={{ inputLabel: { shrink: !!values.configuration?.driveId } }}
          />

          <Field
            component={TextField}
            name="configuration.fileId"
            label={I18n.t('ChatFlowSettings.File ID')}
            placeholder="01ABC123DEF456..."
            helperText="Use the picker above or paste the File ID manually"
            fullWidth
            margin="normal"
            size="small"
            slotProps={{ inputLabel: { shrink: !!values.configuration?.fileId } }}
          />
        </>
      )}

      {/* OneDrive Picker v8 iframe */}
      <PickerPortal />
    </>
  );
};

export default MicrosoftExcelConfig;
